import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatTooltipModule } from "@angular/material/tooltip";
import { catchError, EMPTY, finalize, switchMap } from "rxjs";

import { AccountService } from "@shared/services/account.service";
import { DirectoryApiService } from "@shared/services/directory.api.service";
import { EDirectoryType } from "@shared/types/directory.type";

import { AuthService } from "@app/core/services/auth.service";
import { LocalizationService } from "@app/core/services/localization.service";
import { EToastType, ToastService } from "@app/core/services/toast.service";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "under-ui-language-switcher",
  templateUrl: "./language-switcher.component.html",
  styleUrl: "./language-switcher.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [AsyncPipe, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule, TranslatePipe],
})
export class LanguageSwitcherComponent {
  private readonly localizationService = inject(LocalizationService);
  private readonly accountService = inject(AccountService);
  private readonly directoryApiService = inject(DirectoryApiService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly languages$ = this.localizationService.languages$;
  protected readonly currentLanguage$ = this.localizationService.language$;
  protected isSaving = false;

  onSelectLanguage(code: string, currentLang: string | null): void {
    if (this.isSaving || code === currentLang) {
      return;
    }

    if (!this.authService.getAccessToken()) {
      this.applyLanguage(code);
      return;
    }

    this.isSaving = true;
    const apiLanguage = this.localizationService.toApiLanguage(code);

    this.directoryApiService.getType(EDirectoryType.Language).pipe(
      switchMap(response => {
        const language = response.data?.find(item => item.code === apiLanguage);
        if (response.error || !language?.id) {
          this.showSaveError();
          return EMPTY;
        }

        return this.accountService.changeLanguage({ languageId: language.id });
      }),
      // HTTP failures are shown by the global interceptor; keep the current UI language.
      catchError(() => EMPTY),
      finalize(() => {
        this.isSaving = false;
        this.changeDetectorRef.markForCheck();
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(response => {
      if (response.error || response.data !== true) {
        this.showSaveError();
        return;
      }

      this.applyLanguage(code);
    });
  }

  private applyLanguage(code: string): void {
    this.localizationService.setLang(code);
    window.location.reload();
  }

  private showSaveError(): void {
    this.toastService.i10nToast(EToastType.Error, "LANG.SAVE_FAILED");
  }
}
